import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  applyAndStoreAppearanceMode,
  applyAppearanceMode,
  appearanceModeFromPerson,
  isAppearanceMode,
  readStoredAppearanceMode,
  resolveTheme,
} from "./appearance";

const ThemeContext = createContext({
  appearanceMode: "system",
  resolvedTheme: "light",
  setAppearanceMode: () => {},
  syncFromPerson: () => {},
});

export function ThemeProvider({ children }) {
  const [appearanceMode, setAppearanceModeState] = useState(() => readStoredAppearanceMode());
  const [resolvedTheme, setResolvedTheme] = useState(() =>
    resolveTheme(readStoredAppearanceMode())
  );

  const applyMode = useCallback((mode) => {
    const normalized = applyAndStoreAppearanceMode(mode);
    setAppearanceModeState(normalized);
    setResolvedTheme(resolveTheme(normalized));
    return normalized;
  }, []);

  const setAppearanceMode = useCallback(
    (mode) => {
      if (!isAppearanceMode(mode)) return appearanceMode;
      return applyMode(mode);
    },
    [applyMode, appearanceMode]
  );

  const syncFromPerson = useCallback(
    (person) => {
      const fromServer = appearanceModeFromPerson(person);
      if (!fromServer) return appearanceMode;
      return applyMode(fromServer);
    },
    [applyMode, appearanceMode]
  );

  useEffect(() => {
    applyAppearanceMode(appearanceMode);
    setResolvedTheme(resolveTheme(appearanceMode));
  }, [appearanceMode]);

  useEffect(() => {
    const onExternal = (e) => {
      const mode = e?.detail?.mode;
      if (!isAppearanceMode(mode)) return;
      setAppearanceModeState(mode);
      setResolvedTheme(resolveTheme(mode));
    };
    window.addEventListener("cm-appearance-mode", onExternal);
    return () => window.removeEventListener("cm-appearance-mode", onExternal);
  }, []);

  useEffect(() => {
    if (appearanceMode !== "system") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      applyAppearanceMode("system");
      setResolvedTheme(resolveTheme("system"));
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [appearanceMode]);

  const value = useMemo(
    () => ({
      appearanceMode,
      resolvedTheme,
      setAppearanceMode,
      syncFromPerson,
    }),
    [appearanceMode, resolvedTheme, setAppearanceMode, syncFromPerson]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
